import { useBackgroundQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import {
  WasteSubmissionsSummaryQuery,
  WasteSubmissionsByStateQuery,
  WasteSubmissionsByGeneratorStatusQuery,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { gql } from "generated-graphql";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from "react";
import invariant from "tiny-invariant";

// Default statuses (exclude FILING_COMPLETE and NOT_REPORTING)
// This matches the table's default submissionStatus filter
const DEFAULT_STATUSES = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
];

// Queries moved here to be centralized in the context
// Charts use the same default filters as the table (current year, non-completed statuses)
// to ensure counts are consistent between the overview charts and the data grid
export const WASTE_SUBMISSIONS_SUMMARY_QUERY = gql(`
  query WasteSubmissionsSummary($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byStatus {
        status
        count
      }
      total
    }
  }
`);

export const WASTE_SUBMISSIONS_BY_STATE_QUERY = gql(`
  query WasteSubmissionsByState($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byState {
        state
        count
      }
      total
    }
  }
`);

export const WASTE_SUBMISSIONS_BY_GENERATOR_STATUS_QUERY = gql(`
  query WasteSubmissionsByGeneratorStatus($tenantId: ID!, $filter: WasteSubmissionsSummaryFilter) {
    wasteSubmissionsSummary(tenantId: $tenantId, filter: $filter) {
      byGeneratorStatus {
        generatorStatus
        count
      }
      total
    }
  }
`);

type WasteSubmissionsOverviewContextType = {
  summaryQuery: QueryRef<WasteSubmissionsSummaryQuery>;
  byStateQuery: QueryRef<WasteSubmissionsByStateQuery>;
  byGeneratorStatusQuery: QueryRef<WasteSubmissionsByGeneratorStatusQuery>;
};

const WasteSubmissionsOverviewContext = createContext<
  WasteSubmissionsOverviewContextType | undefined
>(undefined);

export function useWasteSubmissionsOverviewContext() {
  const context = useContext(WasteSubmissionsOverviewContext);
  if (!context) {
    throw new Error(
      "useWasteSubmissionsOverviewContext must be used within a WasteSubmissionsOverviewProvider"
    );
  }
  return context;
}

type WasteSubmissionsOverviewProviderProps = {
  children: ReactNode;
};

export function WasteSubmissionsOverviewProvider({
  children,
}: WasteSubmissionsOverviewProviderProps) {
  const { tenantId } = useTenant();
  invariant(tenantId, "tenantId is required");

  // Build default filter to match the table's default filtering behavior:
  // - Only show non-completed statuses (exclude FILING_COMPLETE, NOT_REPORTING)
  // - Only show current year's submissions
  // This ensures chart counts match table counts when using default filters
  const defaultFilter = useMemo(() => {
    const now = DateTime.now();
    return {
      statuses: DEFAULT_STATUSES,
      dueDateStart: now.startOf("year").toISO(),
      dueDateEnd: now.endOf("year").toISO(),
    };
  }, []);

  const [summaryQuery] = useBackgroundQuery(WASTE_SUBMISSIONS_SUMMARY_QUERY, {
    variables: {
      tenantId,
      filter: defaultFilter,
    },
  });

  const [byStateQuery] = useBackgroundQuery(WASTE_SUBMISSIONS_BY_STATE_QUERY, {
    variables: {
      tenantId,
      filter: defaultFilter,
    },
  });

  const [byGeneratorStatusQuery] = useBackgroundQuery(
    WASTE_SUBMISSIONS_BY_GENERATOR_STATUS_QUERY,
    {
      variables: {
        tenantId,
        filter: defaultFilter,
      },
    }
  );

  const value = useMemo(
    () => ({
      summaryQuery,
      byStateQuery,
      byGeneratorStatusQuery,
    }),
    [summaryQuery, byStateQuery, byGeneratorStatusQuery]
  );

  return (
    <WasteSubmissionsOverviewContext.Provider value={value}>
      {children}
    </WasteSubmissionsOverviewContext.Provider>
  );
}
